import { useState, useEffect } from "react";
import { BiBookOpen, BiPlus, BiSearch } from "react-icons/bi";
import { FiAlertTriangle } from "react-icons/fi";
import { CgClose } from "react-icons/cg";

import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory as deleteCategoryApi,
} from "../hooks/helpCenterCategory.api";
import {
  createSubcategory,
  updateSubcategory,
  deleteSubcategory as deleteSubcategoryApi,
} from "../hooks/helpCenterSubcategory.api";
import {
  fetchArticleById,
  createArticle,
  updateArticle,
  deleteArticle as deleteArticleApi,
} from "../hooks/helpCenterArticle.api";
import { SidebarCategoryBlock } from "../partials/helpCenter/sideBars/categorySideBar";
import { CategoryDetail } from "../partials/helpCenter/details/categoryDetails";
import { SubcategoryDetail } from "../partials/helpCenter/details/subCategoryDetails";
import { ArticleDetail } from "../partials/helpCenter/details/articleDetails";
import { CategoryModal } from "../partials/helpCenter/modals/categoryModal";
import { SubcategoryModal } from "../partials/helpCenter/modals/subCategoryModal";
import { ArticleModal } from "../partials/helpCenter/modals/articleModal";
import { ConfirmDialog } from "../partials/helpCenter/confirmDialog";

const MAX_ARTICLE_CONTENT_BYTES = 700 * 1024;

const flattenCategoriesTree = (nestedCategories) => {
  const categories = [];
  const subcategories = [];
  const articles = [];

  for (const cat of nestedCategories || []) {
    const { subcategories: subs, categoryarticles, ...categoryFields } = cat;
    categories.push(categoryFields);

    (subs || []).forEach((sub) => {
      const { subcategoryarticles, ...subFields } = sub;
      subcategories.push(subFields);
      (subcategoryarticles || []).forEach((article) => articles.push(article));
    });

    (categoryarticles || []).forEach((article) => articles.push(article));
  }

  return { categories, subcategories, articles };
};

const unwrapResource = (payload, ...envelopeKeys) => {
  if (!payload || typeof payload !== "object") return payload;
  if ("id" in payload) return payload;
  for (const key of envelopeKeys) {
    if (payload[key] && typeof payload[key] === "object") return payload[key];
  }
  if (payload.data && typeof payload.data === "object") return payload.data;
  if (payload.result && typeof payload.result === "object") return payload.result;
  return payload;
};

// Small helper so every catch block can surface a user-facing message
// instead of only logging to the console.
const describeError = (error, fallback) =>
  (error && error.message) || fallback || "Something went wrong. Please try again.";

export const Button = ({ variant = "primary", size = "md", className = "", children, ...props }) => {
  const base =
    "inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-cyan-600";
  const sizes = { sm: "text-xs px-2.5 py-1.5", md: "text-sm px-3.5 py-2", lg: "text-sm px-4 py-2.5" };
  const variants = {
    primary: "bg-slate-900 text-white hover:bg-slate-800",
    secondary: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
    ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
    dangerGhost: "text-rose-600 hover:bg-rose-50",
  };
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

export default function HelpCenter() {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [articles, setArticles] = useState([]);

  const [selection, setSelection] = useState({ type: null, id: null });
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState(new Set());
  const [search, setSearch] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [categoryModal, setCategoryModal] = useState({ open: false, initial: null });
  const [subcategoryModal, setSubcategoryModal] = useState({ open: false, initial: null, defaultCategoryId: null });
  const [articleModal, setArticleModal] = useState({ open: false, initial: null, defaultCategoryId: null, defaultSubcategoryId: null });
  const [confirmState, setConfirmState] = useState(null); // { title, message, onConfirm }

  const loadAll = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const tree = await fetchCategories();
      const { categories: cats, subcategories: subs, articles: arts } = flattenCategoriesTree(tree);
      setCategories(cats);
      setSubcategories(subs);
      setArticles(arts);
      if (cats.length) {
        setSelection({ type: "category", id: cats[0].id });
        setExpandedCategories(new Set([cats[0].id]));
      }
    } catch (error) {
      console.error(error?.message);
      // FIX: loadError state was never actually set, so the dedicated
      // error screen below was unreachable even when loading failed.
      setLoadError(describeError(error, "Couldn't load the help center."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const getCategory = (id) => categories.find((c) => c.id === id) || null;
  const getSubcategory = (id) => subcategories.find((s) => s.id === id) || null;
  const subcategoriesOf = (catId) => subcategories.filter((s) => s.categoryId === catId);
  const articlesOfCategory = (catId) => articles.filter((a) => a.categoryId === catId);
  const directArticlesOfCategory = (catId) => articles.filter((a) => a.categoryId === catId && !a.subcategoryId);
  const articlesOfSubcategory = (subId) => articles.filter((a) => a.subcategoryId === subId);

  const query = search.trim().toLowerCase();
  const matches = (text) => !query || text.toLowerCase().includes(query);
  const visibleCategories = () => {
    if (!query) return categories;
    return categories.filter((c) => {
      if (matches(c.name)) return true;
      const subs = subcategoriesOf(c.id);
      if (subs.some((s) => matches(s.name))) return true;
      return articlesOfCategory(c.id).some((a) => matches(a.title));
    });
  };

  const visibleSubcategoriesOf = (catId) => {
    const subs = subcategoriesOf(catId);
    if (!query) return subs;
    return subs.filter((s) => matches(s.name) || articlesOfSubcategory(s.id).some((a) => matches(a.title)));
  };
  const visibleDirectArticlesOf = (catId) => {
    const arts = directArticlesOfCategory(catId);
    if (!query) return arts;
    return arts.filter((a) => matches(a.title));
  };
  const visibleArticlesOfSubcategory = (subId) => {
    const arts = articlesOfSubcategory(subId);
    if (!query) return arts;
    return arts.filter((a) => matches(a.title));
  };

  const toggleCategory = (id) =>
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleSubcategory = (id) =>
    setExpandedSubcategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const ensureExpanded = (catId, subId) => {
    setExpandedCategories((prev) => new Set(prev).add(catId));
    if (subId) setExpandedSubcategories((prev) => new Set(prev).add(subId));
  };

  const selectCategory = (cat) => setSelection({ type: "category", id: cat.id });
  const selectSubcategory = (sub) => setSelection({ type: "subcategory", id: sub.id });
  const selectArticle = (art) => {
    setSelection({ type: "article", id: art.id });
    fetchArticleById(art.id)
      .then((full) => {
        const resolved = unwrapResource(full, "article");
        if (resolved) setArticles((prev) => prev.map((a) => (a.id === art.id ? { ...a, ...resolved } : a)));
      })
      .catch((error) => {
        console.error(error?.message);
        setActionError(describeError(error, "Couldn't load the full article."));
      });
  };

  const saveCategory = async (data) => {
    try {
      if (categoryModal.initial) {
        const updated = unwrapResource(await updateCategory(categoryModal.initial.id, data), "category");
        setCategories((prev) => prev.map((c) => (c.id === categoryModal.initial.id ? { ...c, ...updated } : c)));
      } else {
        const created = unwrapResource(await createCategory(data), "category");
        setCategories((prev) => [...prev, created]);
        setExpandedCategories((prev) => new Set(prev).add(created.id));
        setSelection({ type: "category", id: created.id });
      }
      setCategoryModal({ open: false, initial: null });
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't save the category."));
    }
  };

  const deleteCategory = async (cat) => {
    try {
      await deleteCategoryApi(cat.id);
      const subIds = subcategoriesOf(cat.id).map((s) => s.id);
      setSubcategories((prev) => prev.filter((s) => s.categoryId !== cat.id));
      setArticles((prev) => prev.filter((a) => a.categoryId !== cat.id));
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      if (
        (selection.type === "category" && selection.id === cat.id) ||
        (selection.type === "subcategory" && subIds.includes(selection.id)) ||
        (selection.type === "article" && articlesOfCategory(cat.id).some((a) => a.id === selection.id))
      ) {
        setSelection({ type: null, id: null });
      }
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't delete the category."));
    }
    setConfirmState(null);
  };

  const saveSubcategory = async (data) => {
    try {
      if (subcategoryModal.initial) {
        const updated = unwrapResource(await updateSubcategory(subcategoryModal.initial.id, data), "subcategory");
        setSubcategories((prev) => prev.map((s) => (s.id === subcategoryModal.initial.id ? { ...s, ...updated } : s)));
      } else {
        const created = unwrapResource(await createSubcategory(data), "subcategory");
        setSubcategories((prev) => [...prev, created]);
        ensureExpanded(data.categoryId, created.id);
        setSelection({ type: "subcategory", id: created.id });
      }
      setSubcategoryModal({ open: false, initial: null, defaultCategoryId: null });
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't save the subcategory."));
    }
  };

  const deleteSubcategory = async (sub) => {
    try {
      await deleteSubcategoryApi(sub.id);
      setArticles((prev) => prev.filter((a) => a.subcategoryId !== sub.id));
      setSubcategories((prev) => prev.filter((s) => s.id !== sub.id));
      if (
        (selection.type === "subcategory" && selection.id === sub.id) ||
        (selection.type === "article" && articlesOfSubcategory(sub.id).some((a) => a.id === selection.id))
      ) {
        setSelection({ type: "category", id: sub.categoryId });
      }
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't delete the subcategory."));
    }
    setConfirmState(null);
  };

  const saveArticle = async (data) => {
    const contentBytes = new Blob([data.content || ""]).size;
    if (contentBytes > MAX_ARTICLE_CONTENT_BYTES) {
      setActionError(
        `This article is ~${(contentBytes / 1024).toFixed(0)}KB, over the ${(MAX_ARTICLE_CONTENT_BYTES / 1024).toFixed(
          0
        )}KB limit — remove or further compress an image and try again.`
      );
      return;
    }
    try {
      if (articleModal.initial) {
        const updated = unwrapResource(await updateArticle(articleModal.initial.id, data), "article");
        setArticles((prev) => prev.map((a) => (a.id === articleModal.initial.id ? { ...a, ...updated } : a)));
        setSelection({ type: "article", id: articleModal.initial.id });
      } else {
        const created = unwrapResource(await createArticle(data), "article");
        setArticles((prev) => [...prev, created]);
        ensureExpanded(data.categoryId, data.subcategoryId);
        setSelection({ type: "article", id: created.id });
      }
      setArticleModal({ open: false, initial: null, defaultCategoryId: null, defaultSubcategoryId: null });
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't save the article."));
    }
  };

  const deleteArticle = async (art) => {
    try {
      await deleteArticleApi(art.id);
      setArticles((prev) => prev.filter((a) => a.id !== art.id));
      if (selection.type === "article" && selection.id === art.id) {
        setSelection(art.subcategoryId ? { type: "subcategory", id: art.subcategoryId } : { type: "category", id: art.categoryId });
      }
    } catch (error) {
      console.error(error?.message);
      setActionError(describeError(error, "Couldn't delete the article."));
    }
    setConfirmState(null);
  };

  const confirmDeleteCategory = (cat) => {
    const subCount = subcategoriesOf(cat.id).length;
    const artCount = articlesOfCategory(cat.id).length;
    setConfirmState({
      title: `Delete "${cat.name}"?`,
      message: `This will permanently delete this category${subCount ? `, ${subCount} subcategor${subCount > 1 ? "ies" : "y"}` : ""}${artCount ? `, and ${artCount} article${artCount > 1 ? "s" : ""}` : ""
        }.`,
      onConfirm: () => deleteCategory(cat),
    });
  };
  const confirmDeleteSubcategory = (sub) => {
    const artCount = articlesOfSubcategory(sub.id).length;
    setConfirmState({
      title: `Delete "${sub.name}"?`,
      message: `This will permanently delete this subcategory${artCount ? ` and ${artCount} article${artCount > 1 ? "s" : ""}` : ""}.`,
      onConfirm: () => deleteSubcategory(sub),
    });
  };
  const confirmDeleteArticle = (art) => {
    setConfirmState({
      title: `Delete "${art.title}"?`,
      message: "This will permanently delete this article.",
      onConfirm: () => deleteArticle(art),
    });
  };

  const selectedCategory = selection.type === "category" ? getCategory(selection.id) : null;
  const selectedSubcategory = selection.type === "subcategory" ? getSubcategory(selection.id) : null;
  const selectedArticle = selection.type === "article" ? articles.find((a) => a.id === selection.id) : null;
  const totalArticles = articles.length;
  const visibleCats = visibleCategories();

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50 text-slate-900" style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-cyan-600" />
          <p className="text-sm">Loading your help center&hellip;</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50 text-slate-900 px-6" style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
        <div className="max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <FiAlertTriangle size={20} />
          </div>
          <h2 className="text-sm font-semibold text-slate-900">Couldn&rsquo;t load the help center</h2>
          <p className="mt-1.5 text-sm text-slate-500">{loadError}</p>
          <Button size="sm" className="mt-4" onClick={loadAll}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50 text-slate-900" style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
      {/* ---------------- Sidebar ---------------- */}
      <aside className="flex w-80 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
            <BiBookOpen size={16} />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">Help Center</p>
            <p className="text-xs leading-tight text-slate-400">
              {categories.length} categories &middot; {totalArticles} articles
            </p>
          </div>
        </div>

        <div className="border-b border-slate-100 p-3">
          <div className="relative">
            <BiSearch size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles, categories..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:bg-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {/* FIX: use the computed array (visibleCats), not the function itself */}
          {visibleCats.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate-400">No results</p>
          )}
          {visibleCats.map((cat) => (
            <SidebarCategoryBlock
              key={cat.id}
              category={cat}
              subcategories={visibleSubcategoriesOf(cat.id)}
              directArticles={visibleDirectArticlesOf(cat.id)}
              articlesBySubcategory={visibleArticlesOfSubcategory}
              selection={selection}
              expandedCategories={expandedCategories}
              expandedSubcategories={expandedSubcategories}
              toggleCategory={toggleCategory}
              toggleSubcategory={toggleSubcategory}
              onSelectCategory={() => selectCategory(cat)}
              onSelectSubcategory={selectSubcategory}
              onSelectArticle={selectArticle}
              onEditCategory={() => setCategoryModal({ open: true, initial: cat })}
              onDeleteCategory={() => confirmDeleteCategory(cat)}
              onAddSubcategory={() => setSubcategoryModal({ open: true, initial: null, defaultCategoryId: cat.id })}
              onEditSubcategory={(s) => setSubcategoryModal({ open: true, initial: s, defaultCategoryId: s.categoryId })}
              onDeleteSubcategory={confirmDeleteSubcategory}
              onAddArticle={(subId) =>
                setArticleModal({ open: true, initial: null, defaultCategoryId: cat.id, defaultSubcategoryId: subId || null })
              }
              onEditArticle={(a) =>
                setArticleModal({ open: true, initial: a, defaultCategoryId: a.categoryId, defaultSubcategoryId: a.subcategoryId })
              }
              onDeleteArticle={confirmDeleteArticle}
            />
          ))}
        </div>

        <div className="border-t border-slate-100 p-3">
          <Button className="w-full" size="sm" onClick={() => setCategoryModal({ open: true, initial: null })}>
            <BiPlus size={15} /> New category
          </Button>
        </div>
      </aside>

      {/* ---------------- Main content ---------------- */}
      <main className="flex-1 overflow-y-auto">
        {selection.type === "category" && selectedCategory && (
          <CategoryDetail
            category={selectedCategory}
            subcategories={subcategoriesOf(selectedCategory.id)}
            articles={articlesOfCategory(selectedCategory.id)}
            onEdit={() => setCategoryModal({ open: true, initial: selectedCategory })}
            onDelete={() => confirmDeleteCategory(selectedCategory)}
            onAddSubcategory={() => setSubcategoryModal({ open: true, initial: null, defaultCategoryId: selectedCategory.id })}
            onAddArticle={() =>
              setArticleModal({ open: true, initial: null, defaultCategoryId: selectedCategory.id, defaultSubcategoryId: null })
            }
            onOpenSubcategory={(s) => {
              ensureExpanded(s.categoryId, s.id);
              selectSubcategory(s);
            }}
            onOpenArticle={(a) => {
              ensureExpanded(a.categoryId, a.subcategoryId);
              selectArticle(a);
            }}
          />
        )}

        {selection.type === "subcategory" && selectedSubcategory && (
          <SubcategoryDetail
            subcategory={selectedSubcategory}
            category={getCategory(selectedSubcategory.categoryId)}
            articles={articlesOfSubcategory(selectedSubcategory.id)}
            onEdit={() => setSubcategoryModal({ open: true, initial: selectedSubcategory, defaultCategoryId: selectedSubcategory.categoryId })}
            onDelete={() => confirmDeleteSubcategory(selectedSubcategory)}
            onAddArticle={() =>
              setArticleModal({
                open: true,
                initial: null,
                defaultCategoryId: selectedSubcategory.categoryId,
                defaultSubcategoryId: selectedSubcategory.id,
              })
            }
            onOpenArticle={(a) => selectArticle(a)}
            onBackToCategory={() => selectCategory(getCategory(selectedSubcategory.categoryId))}
          />
        )}

        {selection.type === "article" && selectedArticle && (
          <ArticleDetail
            article={selectedArticle}
            category={getCategory(selectedArticle.categoryId)}
            subcategory={selectedArticle.subcategoryId ? getSubcategory(selectedArticle.subcategoryId) : null}
            onEdit={() =>
              setArticleModal({
                open: true,
                initial: selectedArticle,
                defaultCategoryId: selectedArticle.categoryId,
                defaultSubcategoryId: selectedArticle.subcategoryId,
              })
            }
            onDelete={() => confirmDeleteArticle(selectedArticle)}
            onBack={() =>
              selectedArticle.subcategoryId
                ? selectSubcategory(getSubcategory(selectedArticle.subcategoryId))
                : selectCategory(getCategory(selectedArticle.categoryId))
            }
          />
        )}

        {!selection.type && (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
              <BiBookOpen size={26} />
            </div>
            <h2 className="text-base font-semibold text-slate-900">Select something from the sidebar</h2>
            <p className="mt-1 max-w-sm text-sm text-slate-500">
              Browse categories, subcategories, and articles on the left, or create a new category to get started.
            </p>
          </div>
        )}
      </main>

      <CategoryModal
        open={categoryModal.open}
        initial={categoryModal.initial}
        onClose={() => setCategoryModal({ open: false, initial: null })}
        onSave={saveCategory}
      />
      <SubcategoryModal
        open={subcategoryModal.open}
        initial={subcategoryModal.initial}
        categories={categories}
        defaultCategoryId={subcategoryModal.defaultCategoryId}
        onClose={() => setSubcategoryModal({ open: false, initial: null, defaultCategoryId: null })}
        onSave={saveSubcategory}
      />
      <ArticleModal
        open={articleModal.open}
        initial={articleModal.initial}
        categories={categories}
        subcategories={subcategories}
        defaultCategoryId={articleModal.defaultCategoryId}
        defaultSubcategoryId={articleModal.defaultSubcategoryId}
        onClose={() => setArticleModal({ open: false, initial: null, defaultCategoryId: null, defaultSubcategoryId: null })}
        onSave={saveArticle}
      />
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />

      {actionError && (
        <div className="fixed bottom-4 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-xl border border-rose-200 bg-white px-4 py-3 shadow-lg">
          <FiAlertTriangle size={16} className="shrink-0 text-rose-600" />
          <p className="text-sm text-slate-700">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            className="ml-1 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Dismiss"
            type="button"
          >
            <CgClose size={15} />
          </button>
        </div>
      )}
    </div>
  );
}